import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { DataTable, ListPageLayout, type DataTableColumn } from '@/shared/components/entities'
import { useTranslation } from '@/shared/contexts/TranslationContext'
import { useUnit } from '@/modules/units/contexts/UnitContext'
import { listAccountingClients } from '@/modules/accounting/clients/accountingClientsService'
import type { AccountingClient } from '@/modules/accounting/clients/accountingClientTypes'
import { listAccountingProducts } from '@/modules/accounting/memory/accountingFiscalMemoryService'
import type { AccountingProduct } from '@/modules/accounting/memory/accountingFiscalMemoryTypes'

function ProductIcon() {
  return <svg className="h-7 w-7 text-emerald-700 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="m12 3 8 4.5v9L12 21l-8-4.5v-9L12 3Zm0 0v9m8-4.5-8 4.5m-8-4.5 8 4.5" /></svg>
}

export function AccountingProductsPage() {
  const { partyId } = useParams<{ partyId: string }>()
  const navigate = useNavigate()
  const { t, locale } = useTranslation()
  const { currentUnit } = useUnit()
  const [client, setClient] = useState<AccountingClient | null>(null)
  const [products, setProducts] = useState<AccountingProduct[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [viewMode, setViewMode] = useState<'card' | 'table'>('table')

  const load = useCallback(async () => {
    if (!currentUnit?.id || !partyId) return
    setLoading(true)
    setError(null)
    try {
      const clients = await listAccountingClients(currentUnit.id)
      const selected = clients.find((candidate) => candidate.party_id === partyId)
      if (!selected) throw new Error('accounting_client_not_found')
      setClient(selected)
      setProducts(await listAccountingProducts(selected.client_unit_id))
    } catch (loadError) {
      console.error('[AccountingProductsPage] Load error:', loadError)
      setError(t('accounting.products.errors.load'))
    } finally {
      setLoading(false)
    }
  }, [currentUnit?.id, partyId, t])

  useEffect(() => { void load() }, [load])

  const filtered = useMemo(() => {
    const query = searchQuery.trim().toLocaleLowerCase(locale)
    if (!query) return products
    return products.filter((product) => [
      product.internal_code,
      product.name,
      product.ncm,
      product.cest,
      product.item_purpose_key ? t(`accounting.fiscalMemory.itemTypes.${product.item_purpose_key}`) : null,
    ].some((value) => value?.toLocaleLowerCase(locale).includes(query)))
  }, [locale, products, searchQuery, t])

  const itemType = (product: AccountingProduct) => product.sped_item_type_code
    ? `${product.sped_item_type_code} · ${t(`accounting.fiscalMemory.itemTypes.${product.item_purpose_key || 'unknown'}`)}`
    : t('accounting.fiscalMemory.itemTypes.unknown')
  const source = (product: AccountingProduct) => t(`accounting.fiscalMemory.sources.${product.source_kind}`)
  const columns: DataTableColumn<AccountingProduct>[] = [
    { key: 'code', label: t('accounting.products.columns.code'), render: (product) => <span className="font-semibold text-slate-900 dark:text-white">{product.internal_code || '—'}</span> },
    { key: 'description', label: t('accounting.products.columns.description'), render: (product) => <span className="block max-w-md truncate" title={product.name}>{product.name}</span> },
    { key: 'uom', label: t('accounting.products.columns.uom'), render: (product) => product.uom },
    { key: 'itemType', label: t('accounting.products.columns.itemType'), render: itemType },
    { key: 'ncm', label: t('accounting.products.columns.ncm'), render: (product) => product.ncm || '—' },
    { key: 'source', label: t('accounting.products.columns.source'), render: source },
  ]

  return (
    <ListPageLayout
      title={t('accounting.products.title')}
      description={`${client?.name || ''} · ${t('accounting.products.total', { count: String(products.length) })}`}
      icon={<ProductIcon />}
      backAction={{ label: t('accounting.products.back'), onClick: () => navigate('/accounting') }}
      searchQuery={searchQuery}
      onSearchChange={setSearchQuery}
      searchPlaceholder={t('accounting.products.search')}
      clearSearchLabel={t('list.clearSearch')}
      viewMode={viewMode}
      onViewModeChange={setViewMode}
      onMobileViewOverride={setViewMode}
      showViewToggle
      loading={loading}
      error={error}
      stickyToolbar={!loading ? <p className="px-1 text-sm text-slate-600 dark:text-slate-400">{t('accounting.products.showing', { shown: String(filtered.length), total: String(products.length) })}</p> : undefined}
    >
      {!loading && filtered.length === 0 ? (
        <section className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center dark:border-slate-700 dark:bg-slate-900">
          <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100">{t('accounting.products.emptyTitle')}</h2>
          <p className="mx-auto mt-2 max-w-xl text-sm text-slate-500 dark:text-slate-400">{t('accounting.products.emptyDescription')}</p>
        </section>
      ) : viewMode === 'table' ? (
        <DataTable data={filtered} columns={columns} keyExtractor={(product) => product.asset_id} />
      ) : (
        <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {filtered.map((product) => (
            <article key={product.asset_id} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-700 dark:bg-slate-900">
              <div className="flex items-start justify-between gap-3"><div className="min-w-0"><p className="text-xs font-semibold text-emerald-700 dark:text-emerald-400">{product.internal_code || '—'}</p><h2 className="mt-1 truncate font-semibold text-slate-900 dark:text-white" title={product.name}>{product.name}</h2></div><span className="rounded-full bg-slate-100 px-2 py-1 text-xs text-slate-600 dark:bg-slate-800 dark:text-slate-300">{product.uom}</span></div>
              <dl className="mt-4 grid grid-cols-2 gap-3 text-sm"><div><dt className="text-xs text-slate-500">{t('accounting.products.columns.itemType')}</dt><dd className="mt-1 font-medium text-slate-800 dark:text-slate-200">{itemType(product)}</dd></div><div><dt className="text-xs text-slate-500">{t('accounting.products.columns.ncm')}</dt><dd className="mt-1 font-medium text-slate-800 dark:text-slate-200">{product.ncm || '—'}</dd></div></dl>
              <p className="mt-4 border-t border-slate-100 pt-3 text-xs text-slate-500 dark:border-slate-800 dark:text-slate-400">{source(product)}</p>
            </article>
          ))}
        </section>
      )}
    </ListPageLayout>
  )
}
